import { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/api';

const AuthContext = createContext(null);

const USER_STORAGE_KEY = 'gaza_pharmacy_user';
const TOKEN_STORAGE_KEY = 'gaza_pharmacy_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const clearExpiredSession = () => setUser(null);
    window.addEventListener('gaza-auth-expired', clearExpiredSession);
    return () => window.removeEventListener('gaza-auth-expired', clearExpiredSession);
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      const authUser = res?.user ?? null;
      const token = res?.token ?? null;

      if (!authUser) {
        throw new Error('Invalid credentials');
      }

      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(authUser));
      if (token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
      }
      window.__gazaAuthRedirecting = false;
      setUser(authUser);
      return authUser;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.__gazaAuthRedirecting = false;
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
